import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, AlertTriangle, Calendar, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { api, formatDateBR } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const KpiCard = ({ icon: Icon, label, value, hint, accent, to, testId }) => {
  const navigate = useNavigate();
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={() => to && navigate(to)}
      onKeyDown={(e) => { if (to && (e.key === "Enter" || e.key === " ")) navigate(to); }}
      className="card-soft cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-midnight"
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
          </div>
          <div className={`p-2 rounded-md ${accent || "bg-slate-100 text-slate-700"}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ConsultorDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/").then(({ data }) => setData(data));
  }, []);

  if (!data) return <Skeleton className="h-96" />;

  const { kpis, extras, contracts } = data;

  return (
    <div className="space-y-6" data-testid="consultor-dashboard">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Meu Painel</h1>
        <p className="text-sm text-slate-600 mt-1">
          Contratos atribuídos a você e pendências técnicas. Clique nos cards para navegar.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiCard
          icon={FileText}
          label="Contratos ativos"
          value={kpis.active_contracts}
          accent="bg-emerald-50 text-emerald-700"
          to="/consultor/contratos"
          testId="kpi-consultor-ativos"
        />
        <KpiCard
          icon={Calendar}
          label="Reuniões da semana"
          value={extras?.meetings_this_week ?? 0}
          hint="Próximos 7 dias"
          accent="bg-blue-50 text-blue-700"
          to="/secretaria/agenda"
          testId="kpi-consultor-reunioes"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Ajustes do Controle"
          value={extras?.legal_changes_pending ?? 0}
          accent="bg-amber-50 text-amber-700"
          to="/consultor/contratos"
          testId="kpi-consultor-ajustes-controle"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Em risco (<30d)"
          value={kpis.in_risk}
          accent="bg-red-50 text-red-700"
          to="/consultor/contratos?status=at_risk"
          testId="kpi-consultor-em-risco"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Em atraso"
          value={kpis.overdue || 0}
          accent="bg-rose-100 text-rose-800"
          hint="Prazo vencido"
          to="/consultor/contratos?status=overdue"
          testId="kpi-consultor-em-atraso"
        />
      </div>

      {(extras?.contracts_overdue?.length || 0) > 0 && (
        <Card className="card-soft border-rose-200" data-testid="overdue-consultor-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-rose-800">
              <AlertTriangle className="w-4 h-4" /> Contratos em atraso ({extras.contracts_overdue.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {extras.contracts_overdue.map((c) => (
                <Link
                  key={c.id}
                  to={`/consultor/contratos/${c.id}`}
                  className="flex items-center justify-between p-3 border border-rose-200 rounded-md hover:bg-rose-50/50"
                  data-testid={`overdue-item-${c.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {c.client_name} <span className="text-slate-400">·</span> {c.product_name}
                    </div>
                    <div className="text-xs text-slate-500">
                      Venceu em {formatDateBR(c.end_date)}
                    </div>
                  </div>
                  <span className="text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                    {c.days_overdue} dia{c.days_overdue === 1 ? "" : "s"} em atraso
                  </span>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-soft">
        <CardHeader>
          <CardTitle className="text-base">Meus contratos</CardTitle>
        </CardHeader>
        <CardContent>
          {contracts.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              Nenhum contrato atribuído a você no momento.
            </div>
          ) : (
            <div className="space-y-3">
              {contracts.map((c) => (
                <Link
                  key={c.id}
                  to={`/consultor/contratos/${c.id}`}
                  className="block p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                  data-testid={`consultor-contract-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Building2 className="w-4 h-4 text-slate-500" />
                        <span className="font-medium text-slate-900">{c.client_name}</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-sm text-slate-600">{c.product_name}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <StatusBadge status={c.status} />
                        <StatusBadge status={c.risk} />
                      </div>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      <div>Prazo: {formatDateBR(c.end_date)}</div>
                      <div className={c.days_remaining < 30 ? "text-red-600 font-medium" : ""}>
                        {c.days_remaining < 0 ? "Vencido" : `${c.days_remaining} dias`}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <Progress value={c.progress_pct} className="h-1.5 flex-1" />
                    <span className="text-xs text-slate-600 w-12 text-right">{c.progress_pct}%</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
